/*
 * Copyright (c) 2025. World Wide Generation Ltd
 */

import React from 'react';
import { Button, Badge, Row, Col, Input, FormGroup } from 'reactstrap';
import { DATE, formatDateUTC } from '../../../../../../utils/date';
import { DataIntegrationConnection } from '../../../../../../api/initiative-data-integrations';
import { BasicAlert } from '@g17eco/molecules/alert';
import classNames from 'classnames';
import { useAppSelector } from '../../../../../../reducers';
import { isStaff } from '../../../../../../selectors/user';

interface DataConnectionCardProps {
  connection: DataIntegrationConnection;
  lastSyncAction: 'sync' | 'resync' | null;
  testingConnectionId: string | null;
  onDeleteConnection: (connectionId: string, connectionName: string) => void;
  onPauseResumeConnection: (connectionId: string, connectionName: string, isPaused: boolean) => void;
  onSyncConnection: (connectionId: string, connectionName: string) => void;
  onResyncConnection: (connectionId: string, connectionName: string) => void;
  onTestConnection: (connectionId: string, connectionName: string) => void;
  onViewSchemas: (connectionId: string, connectionName: string) => void;
}

export const DataConnectionCard: React.FC<DataConnectionCardProps> = ({
  connection,
  lastSyncAction,
  testingConnectionId,
  onDeleteConnection,
  onPauseResumeConnection,
  onSyncConnection,
  onResyncConnection,
  onTestConnection,
  onViewSchemas,
}) => {
  const isStaffUser = useAppSelector(isStaff);

  const getStatusBadge = (status: DataIntegrationConnection['status']) => {
    const setupState = status.setup_state;
    const syncState = status.sync_state;

    if (setupState === 'connected' && syncState === 'syncing') {
      return (
        <Badge color='success'>
          <i className='fal fa-sync-alt fa-spin me-1' />
          Syncing...
        </Badge>
      );
    } else if (setupState === 'connected' && syncState === 'scheduled') {
      return <Badge color='success'>Active</Badge>;
    } else if (setupState === 'incomplete') {
      return <Badge color='warning'>Setup Incomplete</Badge>;
    } else if (syncState === 'paused') {
      return <Badge color='secondary'>Paused</Badge>;
    } else if (syncState === 'sync_error') {
      return <Badge color='danger'>Sync Error</Badge>;
    } else {
      return <Badge color='info'>{syncState || setupState || 'Unknown'}</Badge>;
    }
  };

  const isConnectionSyncing = connection.status.sync_state === 'syncing' && lastSyncAction !== 'resync';
  const isConnectionResyncing = connection.status.sync_state === 'resyncing' && lastSyncAction === 'resync';

  const getSyncFrequencyText = (frequency: number) => {
    if (frequency <= 60) return `${frequency} min`;
    if (frequency <= 1440) return `${Math.round(frequency / 60)} hr`;
    return `${Math.round(frequency / 1440)} day`;
  };

  const handleTestConnection = () => {
    onTestConnection(connection.id, connection.schema ?? connection.id);
  };

  const handlePauseResumeConnection = () => {
    onPauseResumeConnection(connection.id, connection.schema ?? connection.id, connection.paused);
  };

  const handleViewSchemas = () => {
    const name = connection.schema ?? connection.id;
    onViewSchemas(connection.id, name);
  };

  return (
    <div className='mb-3 border shadow-sm p-4 rounded-4'>
      <Row className='align-items-start border-bottom pb-2 mb-3'>
        <Col lg={4}>
          <h4>
            <i className='fal fa-layer-group me-2 text-primary' />
            <strong>{connection.schema ?? connection.id}</strong>
          </h4>
        </Col>
        <Col className='d-flex justify-content-end'>
          <div className='connection-actions-top d-flex align-items-end gap-4'>
            {/* Test Button */}
            <div className='d-flex flex-column align-items-center'>
              <button
                className='icon-action-btn mb-1'
                onClick={handleTestConnection}
                disabled={isConnectionSyncing || testingConnectionId === connection.id}
                style={{
                  background: 'none',
                  border: 'none',
                  outline: 'none',
                  cursor:
                    isConnectionSyncing || testingConnectionId === connection.id
                      ? 'not-allowed'
                      : 'pointer',
                  padding: 0,
                }}
              >
                {testingConnectionId === connection.id ? (
                  <i className='fa-2x text-primary fas fa-circle-notch fa-spin' />
                ) : (
                  <i className='fa-2x text-muted fas fa-stethoscope' />
                )}
              </button>
              <span className='small text-muted' style={{ fontWeight: 500 }}>
                {testingConnectionId === connection.id ? 'TESTING' : 'TEST'}
              </span>
            </div>

            {/* Sync Now Icon Button */}
            <div className='d-flex flex-column align-items-center'>
              <button
                className='icon-action-btn mb-1'
                onClick={() => onSyncConnection(connection.id, connection.schema ?? connection.id)}
                disabled={isConnectionSyncing}
                style={{
                  background: 'none',
                  border: 'none',
                  outline: 'none',
                  cursor: isConnectionSyncing ? 'not-allowed' : 'pointer',
                  padding: 0,
                }}
              >
                <i
                  className={classNames(
                    'fa-2x',
                    {
                      'text-primary': isConnectionSyncing,
                      'text-secondary': isConnectionResyncing,
                    },
                    'fas fa-sync',
                    { 'fa-spin': isConnectionSyncing },
                  )}
                />
              </button>
              <span className='small text-muted' style={{ fontWeight: 500 }}>
                {isConnectionSyncing
                  ? 'SYNCING'
                  : isConnectionResyncing
                    ? 'RESYNCING'
                    : 'SYNC NOW'}
              </span>
            </div>
            {/* Pause/Resume Toggle Switch */}
            <div className='d-flex flex-column align-items-center'>
              <FormGroup switch className='mb-1'>
                <Input
                  type='switch'
                  className='form-control-sm'
                  style={{ width: '3rem' }}
                  checked={!connection.paused}
                  disabled={isConnectionSyncing}
                  onChange={handlePauseResumeConnection}
                />
              </FormGroup>
              <span
                className={`small ${connection.paused ? 'text-muted' : 'text-primary'}`}
                style={{ fontWeight: 500 }}
              >
                {connection.paused ? 'DISABLED' : 'ENABLED'}
              </span>
            </div>
          </div>
        </Col>
      </Row>
      <Row className='align-items-start'>
        {/* Connection Info */}
        <Col lg={4}>
          <div className='connection-header mb-3'>
            <div className='connection-meta'>
              <div className='row mb-1'>
                <div className='col-4 text-muted'>Service:</div>
                <div className='col-8'>
                  <Badge color='secondary'>
                    <i className='fal fa-database me-1' />
                    {connection.service}
                  </Badge>
                </div>
              </div>

              <div className='row mb-1'>
                <div className='col-4 text-muted'>Status:</div>
                <div className='col-8'>{getStatusBadge(connection.status)}</div>
              </div>

              <div className='row mb-1'>
                <div className='col-4 text-muted'>ID:</div>
                <div className='col-8'>
                  <small className='text-muted font-monospace'>{connection.id}</small>
                </div>
              </div>
            </div>
          </div>
        </Col>

        {/* Sync Information */}
        <Col lg={5}>
          <div className='info-grid'>
            <div className='row mb-1'>
              <div className='col-5 text-muted text-nowrap pe-1'>
                <i className='fal fa-sync me-1' />
                Frequency:
              </div>
              <div className='col-7'>
                <strong>{getSyncFrequencyText(connection.sync_frequency)}</strong>
              </div>
            </div>

            {connection.daily_sync_time && (
              <div className='row mb-1'>
                <div className='col-5 text-muted text-nowrap pe-1'>
                  <i className='fal fa-clock me-1' />
                  Daily Sync:
                </div>
                <div className='col-7'>
                  <strong>{connection.daily_sync_time}</strong>
                </div>
              </div>
            )}

            <div className='row mb-1'>
              <div className='col-5 text-muted text-nowrap pe-1'>
                <i className='fal fa-check-circle me-1' />
                Last Success:
              </div>
              <div className='col-7'>
                <strong>
                  {connection.succeeded_at
                    ? formatDateUTC(connection.succeeded_at, DATE.DEFAULT_SPACES_WITH_TIME)
                    : 'Never'}
                </strong>
              </div>
            </div>

            {connection.failed_at && (
              <div className='row mb-1'>
                <div className='col-5 text-muted text-nowrap pe-1'>
                  <i className='fal fa-exclamation-circle me-1' />
                  Last Failure:
                </div>
                <div className='col-7'>
                  <strong className='text-danger'>
                    {formatDateUTC(connection.failed_at, DATE.DEFAULT_SPACES_WITH_TIME)}
                  </strong>
                </div>
              </div>
            )}

            <div className='row mb-1'>
              <div className='col-5 text-muted text-nowrap pe-1'>
                <i className='fal fa-calendar-plus me-1' />
                Created:
              </div>
              <div className='col-7'>
                <strong>{formatDateUTC(connection.created_at, DATE.DEFAULT_SPACES_WITH_TIME)}</strong>
              </div>
            </div>

            <div className='row mb-1'>
              <div className='col-5 text-muted text-nowrap pe-1'>
                <i className='fal fa-code-branch me-1' />
                Schema Changes:
              </div>
              <div className='col-7'>
                <strong>{/* TODO: Add schema changes count when available */}0</strong>
              </div>
            </div>

            <div className='row mb-1'>
              <div className='col-5 text-muted text-nowrap'>
                <i className='fal fa-stopwatch me-1' />
                Avg Duration:
              </div>
              <div className='col-7'>
                <strong>{/* TODO: Add average sync duration when available */}--</strong>
              </div>
            </div>
          </div>
        </Col>

        {/* Status Details & Actions */}
        <Col lg={3}>
          {/* Setup Tests Status */}
          {connection.setup_tests && connection.setup_tests.length > 0 ? (
            <div className='mb-3'>
              <div className='row'>
                <div className='col-4 text-muted small'>
                  <strong>Setup Tests:</strong>
                </div>
                <div className='col-8'>
                  {connection.setup_tests.map((test, index) => (
                    <div key={index} className='mb-1'>
                      <Badge color={test.status === 'passed' ? 'success' : 'danger'} className='me-2 small'>
                        {test.status}
                      </Badge>
                      <small>{test.title}</small>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ) : null}

          {/* Data Delay Info */}
          {connection.data_delay_threshold ? (
            <div className='mb-3'>
              <div className='row'>
                <div className='col-4 text-muted small'>
                  <i className='fal fa-hourglass-half me-1' />
                  <strong>Data Delay:</strong>
                </div>
                <div className='col-8'>
                  <small>
                    <strong>{connection.data_delay_sensitivity}</strong>
                    <span className='text-muted'> ({connection.data_delay_threshold}min)</span>
                  </small>
                </div>
              </div>
            </div>
          ) : null}

          {/* Action Buttons */}
          <div className='actions-section'>
            <div className='row'>
              <div className='col-12'>
                {connection.status.setup_state === 'incomplete' ? (
                  // Only Delete action for incomplete setup
                  <div className='d-flex flex-column gap-2'>
                    <Button
                      color='danger'
                      outline
                      size='sm'
                      onClick={() => onDeleteConnection(connection.id, connection.schema)}
                    >
                      <i className='fal fa-trash me-1' />
                      Delete
                    </Button>
                  </div>
                ) : (
                  // Full action set for completed setup
                  <div className='action-buttons d-flex flex-column gap-2'>
                    {/* Re-sync Button */}
                    {isStaffUser ? (
                    <Button
                      outline
                      size='sm'
                      onClick={() => onResyncConnection(connection.id, connection.schema ?? connection.id)}
                      disabled={isConnectionSyncing}
                    >
                      {isConnectionSyncing ? (
                        <>
                          <i className='fal fa-sync-alt fa-spin me-1' />
                          Full Re-sync in progress...
                        </>
                      ) : (
                        <>
                          <i className='fal fa-history me-1' />
                          Full Re-sync
                        </>
                        )}
                      </Button>
                    ) : null}

                    {/* Schemas Button */}
                    <Button
                      outline
                      size='sm'
                      onClick={handleViewSchemas}
                      className='w-100'
                    >
                      <i className='fal fa-eye me-1' />
                      Schemas
                    </Button>

                    {/* Delete Button */}
                    <Button
                      color='danger'
                      outline
                      size='sm'
                      onClick={() => onDeleteConnection(connection.id, connection.schema)}
                      disabled={isConnectionSyncing}
                    >
                      <i className='fal fa-trash me-1' />
                      Delete
                    </Button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </Col>
      </Row>

      {/* Warning Messages */}
      {connection.status.warnings && connection.status.warnings.length > 0 && (
        <Row className='mt-2'>
          <Col>
            <BasicAlert type='warning' className='small mb-0 py-2'>
              <strong>Warnings:</strong>
              <ul className='mb-0 mt-1'>
                {connection.status.warnings.map((warning, index) => (
                  <li key={index}>{warning.message}</li>
                ))}
              </ul>
            </BasicAlert>
          </Col>
        </Row>
      )}

      {/* Error Messages */}
      {connection.status.tasks && connection.status.tasks.length > 0 && (
        <Row className='mt-2'>
          <Col>
            <BasicAlert type='danger' className='small mb-0 py-2'>
              <strong>Issues:</strong>
              <ul className='mb-0 mt-1'>
                {connection.status.tasks.map((task, index) => (
                  <li key={index}>{task.message}</li>
                ))}
              </ul>
            </BasicAlert>
          </Col>
        </Row>
      )}
    </div>
  );
};
